// ──────────────────────────────────────────────────────────────────────
// LumiInterpreter — rules engine that watches the lumiObserver stream
// and decides when (and why) Lumi should speak unprompted.
//
// The interpreter NEVER calls the API. It only detects *which* trigger
// applies and hands off to a callback. LumiTutor.jsx is what owns the
// API call — that separation keeps the rules easy to test and cheap.
//
// Trigger taxonomy (must match the spec):
//   1. REPEATED_MISTAKE      — 3 same-kind failures in a row
//   2. STUCK_IDLE            — 60+ seconds of idle after a rejected attempt
//   3. ABOUT_TO_SUCCEED      — on the right track but one step off
//   4. OVERSHOOT             — completed but wasteful (>= 3× threshold actions)
//   5. WRONG_MENTAL_MODEL    — pattern implies a specific misconception
//   6. STUDENT_MESSAGE       — student typed — bypass everything, instant
// ──────────────────────────────────────────────────────────────────────

import lumiObserver from './lumiObserver';

// Minimum gap between proactive interventions on the same interactive, so
// Lumi doesn't spam — once she's spoken, she waits for the student to act
// before speaking again.
const PROACTIVE_COOLDOWN_MS = 15000;

// Similarity heuristic: two rejected step inputs count as "same kind of
// mistake" if their trimmed forms share a common token pattern or reason.
// This is intentionally loose — the API call gets the full event log
// anyway and can diagnose precisely.
function sameKindOfMistake(a, b) {
  if (!a || !b) return false;
  if (a.payload?.reason && b.payload?.reason && a.payload.reason === b.payload.reason) return true;
  const ai = String(a.payload?.input || '').trim().toLowerCase();
  const bi = String(b.payload?.input || '').trim().toLowerCase();
  if (!ai || !bi) return false;
  if (ai === bi) return true;
  // Same LHS / RHS variable shape?
  const lhsA = ai.split('=')[0]?.trim(), lhsB = bi.split('=')[0]?.trim();
  if (lhsA && lhsB && lhsA === lhsB) return true;
  // Very short edit distance (typo-level) — count as same mistake.
  if (Math.abs(ai.length - bi.length) <= 1 && _editDistance(ai, bi) <= 2) return true;
  return false;
}

function _editDistance(s, t) {
  if (s === t) return 0;
  const n = s.length, m = t.length;
  if (!n) return m; if (!m) return n;
  const dp = Array.from({ length: n + 1 }, (_, i) => [i, ...Array(m).fill(0)]);
  for (let j = 0; j <= m; j++) dp[0][j] = j;
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const c = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + c);
    }
  }
  return dp[n][m];
}

// ── Rule detectors ────────────────────────────────────────────────────
// Each detector receives the recent event log (for one interactive) and
// the triggering event. Returns a trigger descriptor or null.

function detectRepeatedMistake(events) {
  const rejects = events.filter(e => e.type === 'step_rejected');
  if (rejects.length < 3) return null;
  const [a, b, c] = rejects.slice(-3);
  if (sameKindOfMistake(a, b) && sameKindOfMistake(b, c)) {
    return {
      trigger: 'REPEATED_MISTAKE',
      detail: {
        attempts: [a.payload?.input, b.payload?.input, c.payload?.input],
        reasons: [a.payload?.reason, b.payload?.reason, c.payload?.reason],
      },
    };
  }
  return null;
}

function detectStuckIdle(events) {
  // idle_extended (=60s) only counts if the student had a rejected attempt
  // first — idle at the very start is NOT "stuck", it's reading.
  const last = events[events.length - 1];
  if (!last || last.type !== 'idle_extended') return null;
  // Walk back — is there any step_rejected or challenge_failed before the idle?
  for (let i = events.length - 2; i >= 0; i--) {
    const t = events[i].type;
    if (t === 'step_rejected' || t === 'challenge_failed' || t === 'punnett_wrong') {
      return { trigger: 'STUCK_IDLE', detail: { lastFailure: events[i].payload } };
    }
    // If they got a success in the meantime, they're not stuck.
    if (t === 'step_accepted' || t === 'challenge_started' || t === 'equilibrium_reached') break;
  }
  return null;
}

function detectAboutToSucceed(events, triggeringEvent) {
  // Signal from the interactive itself: payload.almost === true
  if (triggeringEvent?.type === 'step_rejected' && triggeringEvent.payload?.almost === true) {
    return { trigger: 'ABOUT_TO_SUCCEED', detail: triggeringEvent.payload };
  }
  return null;
}

function detectOvershoot(events, triggeringEvent) {
  if (triggeringEvent?.type !== 'completed' && triggeringEvent?.type !== 'challenge_completed') return null;
  const actions = events.filter(e =>
    e.type === 'slider_moved' || e.type === 'step_entered' || e.type === 'allele_dragged'
  ).length;
  const threshold = triggeringEvent.payload?.expectedActions ?? 5;
  if (actions > threshold * 3) {
    return { trigger: 'OVERSHOOT', detail: { actions, expected: threshold } };
  }
  return null;
}

function detectWrongMentalModel(events, triggeringEvent) {
  // Canonical pattern: Punnett — student runs a small sample, gets unlucky
  // ratio, then *changes the parents*. They're treating the sample result
  // as the prediction.
  if (triggeringEvent?.type === 'genotype_changed') {
    const recentRuns = events.filter(e => e.type === 'population_run').slice(-3);
    if (recentRuns.length >= 1) {
      const lastRun = recentRuns[recentRuns.length - 1];
      const size = lastRun.payload?.size || 0;
      if (size > 0 && size <= 20) {
        return { trigger: 'WRONG_MENTAL_MODEL', detail: { kind: 'small_sample_generalisation', lastRun: lastRun.payload } };
      }
    }
  }
  // Generic hook — interactives can emit a `misconception_flag` themselves
  if (triggeringEvent?.type === 'misconception_flag') {
    return { trigger: 'WRONG_MENTAL_MODEL', detail: triggeringEvent.payload };
  }
  return null;
}

// ── Interpreter ───────────────────────────────────────────────────────
// Owns the subscription to lumiObserver and the per-interactive cooldown
// state. `onTrigger(trigger, eventContext)` is invoked when a rule fires.

export function createInterpreter({ interactiveId, onTrigger }) {
  if (!interactiveId || typeof onTrigger !== 'function') {
    throw new Error('createInterpreter requires { interactiveId, onTrigger }');
  }

  let lastProactiveAt = 0;
  let disposed = false;

  const unsubscribe = lumiObserver.subscribe((event) => {
    if (disposed) return;
    if (event.interactiveId !== interactiveId) return;

    // Student messages always bypass cooldown.
    if (event.type === 'student_message') {
      onTrigger({ trigger: 'STUDENT_MESSAGE', detail: { text: event.payload?.text || '' } }, event);
      return;
    }

    // Cooldown gate for proactive triggers only.
    const now = Date.now();
    if (now - lastProactiveAt < PROACTIVE_COOLDOWN_MS) return;

    const events = lumiObserver.getRecentForInteractive(interactiveId, 30);

    // Detection order: student_stuck (highest) > wrong model > about to
    // succeed > overshoot > repeated mistake > stuck idle.
    const detectors = [
      () => detectWrongMentalModel(events, event),
      () => detectAboutToSucceed(events, event),
      () => detectOvershoot(events, event),
      () => detectRepeatedMistake(events),
      () => detectStuckIdle(events),
    ];
    for (const d of detectors) {
      const hit = d();
      if (hit) {
        lastProactiveAt = now;
        onTrigger(hit, event);
        return;
      }
    }

    // Highest-severity idle (student_stuck) fires even through cooldown —
    // we've waited 2 minutes, the student is genuinely lost.
    if (event.type === 'student_stuck') {
      lastProactiveAt = now;
      onTrigger({ trigger: 'STUCK_IDLE', detail: { severity: 'student_stuck' } }, event);
    }
  });

  return {
    dispose() {
      disposed = true;
      unsubscribe();
    },
    // For tests — allow forcing the cooldown to reset.
    _resetCooldown() { lastProactiveAt = 0; },
  };
}

// Exposed for tests.
export const __internals = { sameKindOfMistake, detectRepeatedMistake, detectStuckIdle, detectAboutToSucceed, detectOvershoot, detectWrongMentalModel };
